import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  fetchClientAiUsage,
  fetchUserAiUsage,
  calculateQuotaStatus,
  subscribeAiUsageUpdate,
  DEFAULT_MONTHLY_QUOTA_USD,
} from "../services/aiUsageService";

export const useAiUsage = () => {
  const { user, isAuthenticated } = useAuth();
  const username = user?.username;
  const clientId = user?.client_id;
  const role = user?.role;
  const isSuperAdmin = role === "super-admin";
  const isAdmin = role === "admin" || role === "super-admin";

  const [userUsage, setUserUsage] = useState(null);
  const [clientUsage, setClientUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const loadUsage = useCallback(
    async ({ silent = false } = {}) => {
      if (!isAuthenticated || !username) {
        setIsLoading(false);
        return;
      }

      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      try {
        // Fetch user detailed usage and client aggregate in parallel
        const [userData, clientData] = await Promise.allSettled([
          fetchUserAiUsage(username, { clientId }),
          fetchClientAiUsage({ clientId }),
        ]);

        if (!isMountedRef.current) return;

        let hasData = false;
        if (userData.status === "fulfilled" && userData.value) {
          setUserUsage(userData.value);
          hasData = true;
        }

        if (clientData.status === "fulfilled" && clientData.value) {
          const clientVal = { ...clientData.value };
          // Security & scoping: Ensure non-admins do not retain individual user breakdown in state
          if (!isAdmin) {
            clientVal.users = [];
          }
          setClientUsage(clientVal);
          hasData = true;
        }

        if (!hasData && (userData.status === "rejected" || clientData.status === "rejected")) {
          const firstErr =
            userData.status === "rejected"
              ? userData.reason
              : clientData.reason;
          setError(firstErr);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [isAuthenticated, username, clientId, isAdmin]
  );

  useEffect(() => {
    isMountedRef.current = true;
    loadUsage();

    // Re-fetch on global AI usage update event (e.g. after AI response)
    const unsubscribe = subscribeAiUsageUpdate(() => {
      loadUsage({ silent: true });
    });

    return () => {
      isMountedRef.current = false;
      unsubscribe();
    };
  }, [loadUsage]);

  // Client monthly quota applies to total team cost, dynamically derived from backend (client_config)
  const clientQuotaUsd =
    Number(clientUsage?.monthly_quota_usd) ||
    Number(userUsage?.monthly_quota_usd) ||
    DEFAULT_MONTHLY_QUOTA_USD;
  const effectiveCostUsd = clientUsage?.total_cost_usd ?? 0;
  const quotaStatus = calculateQuotaStatus(effectiveCostUsd, clientQuotaUsd);

  const userCostUsd = userUsage?.total_cost_usd ?? 0;
  const userQuotaStatus = calculateQuotaStatus(userCostUsd, clientQuotaUsd);

  return {
    userUsage,
    clientUsage,
    isLoading,
    error,
    refresh: () => loadUsage({ silent: false }),
    quotaStatus,
    userQuotaStatus,
    quota: clientQuotaUsd,
    isAdmin,
    isSuperAdmin,
    username,
    clientId,
  };
};

export default useAiUsage;
